import React, { useEffect, useState } from 'react';
import api from '../api';

function Marketplace() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=720&q=75';

  useEffect(() => {
    let mounted = true;

    const normalizeProduct = (item, index) => {
      if (!item || typeof item !== 'object') return null;

      const title =
        item.productName ||
        item.title ||
        item.name ||
        item.product_title ||
        item.name_en ||
        `Product ${index + 1}`;

      const image =
        item.productImageURL ||
        item.productImageUrl ||
        item.image ||
        item.imageUrl ||
        item.image_url ||
        item.thumbnail ||
        item.img ||
        item.picture ||
        '';

      const url =
        item.productURL ||
        item.productUrl ||
        item.url ||
        item.link ||
        item.product_url ||
        item.redirectUrl ||
        item.redirect_url ||
        item.productLink ||
        item.product_link ||
        '';

      const id = item.id || item.asin || item.sku || url || title;

      return {
        id: String(id),
        title,
        image,
        url,
      };
    };

    const dedupeProducts = (items) => {
      const seen = new Map();
      return items.reduce((acc, item, index) => {
        const normalized = normalizeProduct(item, index);
        if (!normalized) return acc;

        const key = normalized.id || normalized.url || normalized.title.toLowerCase().trim();
        if (!seen.has(key)) {
          seen.set(key, true);
          acc.push(normalized);
        }
        return acc;
      }, []);
    };

    const fetchProducts = async () => {
      try {
        const res = await api.get('/product');
        const data = res?.data || [];

        if (!mounted) return;

        const items = Array.isArray(data) ? data : [data];
        const normalizedItems = dedupeProducts(items).filter((item) => item.url);

        if (normalizedItems.length > 0) {
          setProducts(normalizedItems);
        } else {
          setProducts([]);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err);
        setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <main className="marketplace-page">
      <section className="marketplace-banner">
        <img
          src="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=1400&q=80"
          alt="A bright, welcoming kitchen ready for cooking"
          width="1400"
          height="560"
        />
        <div className="marketplace-banner-copy">
          <span className="eyebrow">Little helpers for joyful cooking</span>
          <h1>Make room for a little kitchen magic.</h1>
          <p>Thoughtful finds from our marketplace to make time in the kitchen even sweeter.</p>
        </div>
      </section>

      <div className="marketplace-content">
        <div className="section-header">
          <h2>Kitchen favourites</h2>
          <p>Explore products available through our marketplace.</p>
        </div>

      {loading && <div>Loading products…</div>}
      {error && (
        <div style={{ color: 'var(--color-danger, #e53e3e)', marginBottom: 12 }}>
          Could not load marketplace products right now. Please try again later.
        </div>
      )}
      {!loading && !error && products.length === 0 && (
        <div className="marketplace-empty">
          <span aria-hidden="true">🧺</span>
          <h3>Something lovely is on its way</h3>
          <p>There are no kitchen finds to show just yet. Please check back soon.</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
        {products.map((p) => {
          const isHovered = hoveredId === p.id;

          return (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onMouseEnter={() => setHoveredId(p.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                borderRadius: 4,
                border: isHovered
                  ? '1px solid var(--color-secondary)'
                  : '1px solid var(--color-border)',
                background: 'var(--color-card)',
                padding: 12,
                boxShadow: isHovered
                  ? '0 12px 24px -6px rgba(23, 37, 30, 0.18), 0 4px 8px -4px rgba(23, 37, 30, 0.08)'
                  : '0 5px 18px rgba(23, 37, 30, 0.06)',
                transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
                transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Image Container */}
              <div
                style={{
                  height: 160,
                  borderRadius: 2,
                  background: 'var(--color-secondary-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 12,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <img
                  src={p.image || PLACEHOLDER_IMAGE}
                  alt={p.image ? p.title : 'A bright home kitchen'}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                  width="720"
                  height="480"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                    transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                />
              </div>

              {/* Title & Metadata */}
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: '1.3',
                  marginBottom: 4,
                  color: isHovered ? 'var(--color-primary)' : 'inherit',
                  transition: 'color 0.2s ease',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {p.title}
              </div>
            </a>
          );
        })}
      </div>
      </div>
    </main>
  );
}

export default Marketplace;